import { createSelector } from 'reselect';
import { List, Map } from 'immutable';
import get from 'lodash/get';

import Partner from 'models/partner';
import FeatureFlags from 'models/feature-flags';
import * as AppContext from 'lib/app-context';
import * as Apps from 'lib/app-names';

const readProp = (slice, key, defaultValue) => {
  if (!slice) {
    return defaultValue;
  }
  if (typeof slice.get === 'function') {
    const val = slice.get(key);
    return val !== undefined ? val : defaultValue;
  }
  return get(slice, key, defaultValue);
};

export const getAppState = state => get(state, 'app', null);

export const getApp = createSelector(
  [getAppState],
  app => readProp(app, 'name', Apps.HOME_APP),
);

export const getAppName = getApp;

export const getAppContext = createSelector(
  [getAppState],
  app => readProp(app, 'appContext', undefined) || readProp(app, 'context', AppContext.DESKTOP),
);

export const getFeatureFlags = createSelector(
  [getAppState],
  app => readProp(app, 'featureFlags', new FeatureFlags()),
);

export const getLocale = createSelector(
  [getAppState],
  app => readProp(app, 'locale', 'en-us'),
);

export const getTranslations = createSelector(
  [getAppState],
  app => readProp(app, 'translations', Map()),
);

export const getCurrentLocaleTranslations = createSelector(
  [getTranslations, getLocale],
  (translations, locale) => {
    if (translations && typeof translations.get === 'function') {
      return translations.get(locale) || {};
    }
    return get(translations, locale, {});
  },
);

export const getPartner = createSelector(
  [getAppState],
  app => readProp(app, 'partner', new Partner()),
);

export const getPartnerName = createSelector(
  [getAppState],
  app => readProp(app, 'partnerName', null),
);

export const getAffiliatePartner = createSelector(
  [getAppState],
  app => readProp(app, 'affiliatePartner', Map()),
);

export const getUnsupportedBrowser = createSelector(
  [getAppState],
  app => Boolean(readProp(app, 'unsupportedBrowser', false)),
);

export const getMeta = createSelector(
  [getAppState],
  app => readProp(app, 'meta', null),
);

export const getParkingLocations = createSelector(
  [getAppState],
  app => readProp(app, 'parkingLocations', List()),
);

export const getFooterQuestions = createSelector(
  [getAppState],
  app => readProp(app, 'footerQuestions', List()),
);

export const getDirectAffiliateTraffic = createSelector(
  [getAppState],
  app => Boolean(readProp(app, 'directAffiliateTraffic', false)),
);

export const getUseJs = createSelector(
  [getAppState],
  app => readProp(app, 'usejs', true),
);

export const getClientSettings = createSelector(
  [getAppState],
  app => readProp(app, 'clientSettings', Map()),
);

export const getPrivacySettings = createSelector(
  [getAppState],
  app => readProp(app, 'privacy', {
    allowAdvertising: undefined,
    allowAnalytics: undefined,
    browserHasGpcSignal: false,
  }),
);

export const getRouterLocation = state => get(state, 'router.location', null);

export const getRequestQueue = state => get(state, 'requests.requestQueue', null);

export const getRequestQueueLength = state => get(state, 'requests.requestQueueLength', 0);

export const getInsights = state => get(state, 'analytics.insights', null);

export const getTrackingProperties = state => get(state, 'analytics.trackingProperties', null);
